import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Loader2, CheckCircle2, AlertTriangle, Mail } from 'lucide-react';
import { checkSellerEmail } from '../../../../../../features/sellers/sellersThunks';
import { isValidEmail } from '../../../../../../features/deliveries/orderUploadUtils';
import { Field, inputClass } from '../components/FormField';

const CHECK_DEBOUNCE_MS = 500;

/**
 * The seller's login.
 *
 * One field, and its own step, because it is the only irreversible thing the
 * form does: it opens a user account and mails that account its password. It
 * is also the only value whose collision is not a clean rejection — the server
 * checks it after having already written the company and its address, and does
 * not undo them — so it is confirmed free here, before anything else is filled
 * in.
 *
 * @param {object} props
 * @param {object} props.form
 * @param {Object.<string, string>} props.errors
 * @param {(name: string, value: string) => void} props.onChange
 * @param {{email: string, status: string, available: boolean|null}} props.emailCheck
 */
export const AccountStep = ({ form, errors, onChange, emailCheck }) => {
  const dispatch = useDispatch();
  const email = String(form.email || '').trim();

  // Debounced so a lookup fires once the typing settles, not per keystroke.
  // Re-entering the step with an address already checked dispatches nothing.
  useEffect(() => {
    if (!isValidEmail(email) || emailCheck.email === email) return undefined;

    const timer = setTimeout(() => {
      dispatch(checkSellerEmail(email));
    }, CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [email, emailCheck.email, dispatch]);

  const isCurrent = emailCheck.email === email;
  const isChecking = isCurrent && emailCheck.status === 'checking';
  const isAvailable = isCurrent && emailCheck.status === 'done' && emailCheck.available === true;
  const isUnknown = isCurrent && emailCheck.status === 'done' && emailCheck.available === null;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Correo del seller" error={errors.email}>
          <input
            type="email"
            value={form.email}
            onChange={(event) => onChange('email', event.target.value)}
            placeholder="Ej: contacto@empresa.cl"
            autoComplete="off"
            className={inputClass}
          />
        </Field>
      </div>

      {isChecking && (
        <p className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 size={15} className="animate-spin" />
          Verificando disponibilidad...
        </p>
      )}

      {isAvailable && (
        <p className="flex items-center gap-2 text-sm text-ninja-green font-medium">
          <CheckCircle2 size={15} />
          Correo disponible
        </p>
      )}

      {isUnknown && (
        <p className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
          <AlertTriangle size={15} />
          No pudimos verificar el correo. Puedes continuar, se validará al crear el seller.
        </p>
      )}

      <div className="flex items-start gap-3 p-4 rounded-xl bg-ninja-gray/50 dark:bg-[#111] border border-border">
        <Mail size={18} className="text-text-muted shrink-0 mt-0.5" />
        <p className="text-sm text-text-secondary">
          A esta dirección le llegará una contraseña temporal para entrar a la plataforma. También la
          verás en pantalla al terminar, por si el correo no llega.
        </p>
      </div>
    </div>
  );
};
